import {
  TRIGGER_ORDER_STARTED,
  TRIGGER_ORDER_PRODUCT_SELECTED,
  TRIGGER_ORDER_DETAILS_ENTERED,
  TRIGGER_ORDER_REVIEWED,
  GTT_BUY_STARTED_BANNER,
  GTT_BUY_STARTED_BUY_PAGE,
  GTT_BUY_SKU_SEARCH,
  GTT_BUY_SKU_SELECTED,
  GTT_BUY_REVIEW_BUTTON,
  GTT_BUY_CREATED,
  GTT_BUY_CANCELLED,
  GTT_BUY_PRICE_TYPED,
  GTT_BUY_QUICK_SELECT_TAPPED,
  GTT_BUY_QTY_ENTRY,
  GTT_BUY_QTY_STEPPER,
  GTT_BUY_VAULT_LOCATION,
  GTT_BUY_INSUFFICIENT_FUNDS,
  GTT_BUY_ADD_FUNDS_TAPPED,
  GTT_BUY_CURRENT_PRICE_TAPPED,
} from '../../../constants/gtmEvents';

/**
 * Centralized GTT (Buy-at-Target-Price / trigger-order) GTM event dispatchers.
 *
 * Mirrors the price-alert gtmEventHelper convention: each function receives the
 * `ecommerceGtmEvent` dispatcher (from `useGtmHook()`) and builds the payload,
 * so the event names and payload shapes live in one place rather than inline
 * across the flow's screens.
 */

// How the trigger-order flow was entered. A single vocabulary so call sites
// can't drift on spelling.
export const ENTRY_POINTS = {
  HEADER: 'header', // "Buy at Target Price" nav item
  PRODUCT_CARD: 'product_card', // per-product card button (future)
  BUY_PAGE: 'buy_page', // Buy-page card
  TOP_NAV_BANNER: 'top_nav_banner', // top-nav "Buy at Target Price" banner
  PORTFOLIO_PRODUCT_PAGE: 'portfolio_product_page', // "Buy at Target Price?"
  // switch-over link on a Portfolio product page's Buy flow — this page has
  // no direct GTT CTA of its own, only this hand-off.
  EXPIRED_ORDER: 'expired_order', // "Set target price again" on an expired
  // Target Price Order's details.
};

// trigger_order_started — the user opened the Buy-at-Target-Price flow.
export const trackTriggerOrderStarted = ({ ecommerceGtmEvent, entryPoint }) => {
  ecommerceGtmEvent(TRIGGER_ORDER_STARTED, {
    entry_point: entryPoint ?? null,
  });
};

// trigger_order_product_selected — the user picked a product and advanced past
// the product-selection screen.
export const trackTriggerOrderProductSelected = ({
  ecommerceGtmEvent,
  productCode,
  productName,
}) => {
  ecommerceGtmEvent(TRIGGER_ORDER_PRODUCT_SELECTED, {
    product_code: productCode,
    product_name: productName,
  });
};

// trigger_order_details_entered — the user set the target price, quantity and
// vault and advanced past the order-entry screen.
export const trackTriggerOrderDetailsEntered = ({
  ecommerceGtmEvent,
  productCode,
  targetPrice,
  quantity,
  vault,
}) => {
  ecommerceGtmEvent(TRIGGER_ORDER_DETAILS_ENTERED, {
    product_code: productCode,
    target_price: targetPrice,
    quantity,
    vault,
  });
};

// trigger_order_reviewed — the user reviewed the order summary and advanced
// past the review screen.
export const trackTriggerOrderReviewed = ({
  ecommerceGtmEvent,
  productCode,
  targetPrice,
  quantity,
  vault,
}) => {
  ecommerceGtmEvent(TRIGGER_ORDER_REVIEWED, {
    product_code: productCode,
    target_price: targetPrice,
    quantity,
    vault,
  });
};

// gtt_buy_started_banner — the user tapped "Buy at Target Price" from the top
// nav banner.
export const trackGttBuyStartedBanner = ({
  ecommerceGtmEvent,
  userId,
  accountType,
  verified,
}) => {
  ecommerceGtmEvent(GTT_BUY_STARTED_BANNER, {
    entry_point: ENTRY_POINTS.TOP_NAV_BANNER,
    user_id: userId,
    account_type: accountType,
    verified,
    timestamp: new Date().toISOString(),
  });
};

// gtt_buy_started_buy_page — the user tapped "Buy at Target Price" on a
// product card on the Buy page.
export const trackGttBuyStartedBuyPage = ({
  ecommerceGtmEvent,
  productId,
  productName,
  weight,
  currentPrice,
  userId,
  accountType,
  verified,
}) => {
  ecommerceGtmEvent(GTT_BUY_STARTED_BUY_PAGE, {
    entry_point: ENTRY_POINTS.BUY_PAGE,
    product_id: productId,
    product_name: productName,
    weight,
    current_price: currentPrice,
    user_id: userId,
    account_type: accountType,
    verified,
    timestamp: new Date().toISOString(),
  });
};

// gtt_buy_sku_search — the user opened the product search on the product
// selection step (banner flow only). No product properties: nothing is chosen
// yet at this point, and the Buy page's own search click carries none either,
// so the two stay consistent.
export const trackGttBuySkuSearch = ({ ecommerceGtmEvent }) => {
  ecommerceGtmEvent(GTT_BUY_SKU_SEARCH, {});
};

// gtt_buy_sku_selected — the user picked a product in the list on the product
// selection step (banner flow only).
export const trackGttBuySkuSelected = ({
  ecommerceGtmEvent,
  productId,
  productName,
  weight,
  currentPrice,
}) => {
  ecommerceGtmEvent(GTT_BUY_SKU_SELECTED, {
    product_id: productId,
    product_name: productName,
    weight,
    current_price: currentPrice,
  });
};

// gtt_buy_review_button — the user reached the Review screen (quote already
// fetched, so order_value/volume_tier are BE-sourced rather than estimated).
export const trackGttBuyReviewButton = ({
  ecommerceGtmEvent,
  productId,
  productName,
  triggerPrice,
  quantity,
  orderValue,
  volumeTier,
  vaultLocation,
  entryPoint,
}) => {
  ecommerceGtmEvent(GTT_BUY_REVIEW_BUTTON, {
    product_id: productId,
    product_name: productName,
    trigger_price: triggerPrice,
    quantity,
    order_value: orderValue,
    volume_tier: volumeTier ?? null,
    vault_location: vaultLocation,
    entry_point: entryPoint ?? null,
  });
};

// gtt_buy_created — the order was created server-side (the POST
// trigger-orders/quotes succeeded). volumeTier is the formatted tier-range
// label (e.g. "$50k–$55k" or "$1M+") the order's total fell into, or null
// when the quote carried no discount. orderId is the created order's
// TriggerOrderUid, the join key gtt_buy_cancelled reports back for the same
// order. Every key is always sent (null rather than omitted when unknown):
// ecommerceGtmEvent pushes these at the top level of the dataLayer, which GTM
// retains key-by-key across pushes — an omitted key reads as whatever the
// previous event left there, not as "no value" (e.g. a later no-discount
// order would otherwise inherit an earlier order's volume_tier).
export const trackGttBuyCreated = ({
  ecommerceGtmEvent,
  orderId,
  productId,
  productName,
  currentPrice,
  triggerPrice,
  quantity,
  orderValue,
  volumeTier,
  vaultLocation,
  entryPoint,
}) => {
  ecommerceGtmEvent(GTT_BUY_CREATED, {
    order_id: orderId ?? null,
    product_id: productId ?? null,
    product_name: productName ?? null,
    current_price: currentPrice ?? null,
    trigger_price: triggerPrice ?? null,
    quantity: quantity ?? null,
    order_value: orderValue ?? null,
    volume_tier: volumeTier ?? null,
    vault_location: vaultLocation ?? null,
    entry_point: entryPoint ?? null,
  });
};

// gtt_buy_cancelled — the user cancelled an active (not yet triggered) order.
// orderId is the cancelled order's TriggerOrderUid (same value used as the
// cancel API's triggerOrderUid), the join key back to that order's
// gtt_buy_created. trigger_price is derived by the caller as amount /
// quantity (the list API's Amount is the total reserved value, consistent
// with how totalAtExecution is computed elsewhere for the same order type).
// product_id is always null: the pending-trigger-order data this fires from
// has no product/SKU code today (only ProductCaption) — add it once BE
// exposes one. Every key is always sent (null rather than omitted when
// unknown) for the same reason as trackGttBuyCreated: GTM retains an omitted
// key's last-pushed value rather than treating it as "no value" — until
// product_id/trigger_price are genuinely available here, omitting them would
// let GTM silently reuse the previous gtt_buy_created's values instead.
export const trackGttBuyCancelled = ({
  ecommerceGtmEvent,
  orderId,
  productName,
  quantity,
  timeActiveDays,
  triggerPrice,
  orderValue,
}) => {
  ecommerceGtmEvent(GTT_BUY_CANCELLED, {
    order_id: orderId ?? null,
    product_id: null,
    product_name: productName ?? null,
    quantity: quantity ?? null,
    time_active_days: timeActiveDays ?? null,
    trigger_price: triggerPrice ?? null,
    order_value: orderValue ?? null,
  });
};

// gtt_buy_price_typed — the user manually typed a trigger price on the Order
// Entry screen. currentPrice is this screen's own displayed price (vault/tier
// -aware, the same value the below-market 5%/10% buttons and "must be below
// current price" validation are computed from) — deliberately not the flat
// catalog product_id price gtt_buy_created/gtt_buy_started_* use, since
// pct_below_market has to be computed against whatever the user actually saw
// on screen, not a different number. Same choice applies to
// trackGttBuyQuickSelectTapped and trackGttBuyCurrentPriceTapped below.
// pct_below_market is computed here (not by the caller) since it's a pure
// function of the two other fields already being reported; null when
// currentPrice isn't a usable positive number, rather than a divide-by-zero.
export const trackGttBuyPriceTyped = ({
  ecommerceGtmEvent,
  productId,
  triggerPrice,
  currentPrice,
}) => {
  const pctBelowMarket =
    Number(currentPrice) > 0 && triggerPrice != null
      ? ((Number(currentPrice) - Number(triggerPrice)) / Number(currentPrice)) *
        100
      : null;
  ecommerceGtmEvent(GTT_BUY_PRICE_TYPED, {
    product_id: productId ?? null,
    trigger_price: triggerPrice ?? null,
    current_price: currentPrice ?? null,
    pct_below_market: pctBelowMarket,
  });
};

// gtt_buy_quick_select_tapped — the user tapped the 5% or 10% below-market
// quick-select pill. triggerPricePopulated is the price that pill filled in
// (displayPrice × the pill's factor), not the field's prior value.
export const trackGttBuyQuickSelectTapped = ({
  ecommerceGtmEvent,
  productId,
  productName,
  quickSelectPct,
  triggerPricePopulated,
  currentPrice,
}) => {
  ecommerceGtmEvent(GTT_BUY_QUICK_SELECT_TAPPED, {
    product_id: productId ?? null,
    product_name: productName ?? null,
    quick_select_pct: quickSelectPct ?? null,
    trigger_price_populated: triggerPricePopulated ?? null,
    current_price: currentPrice ?? null,
  });
};

// gtt_buy_qty_entry — the user typed a quantity directly. orderValue is the
// client-side quantity × typed-price total shown on screen (no quote exists
// yet at this step); volumeTier is the formatted tier-range label the order
// total's price tier falls in, or null when it's the base (no-discount) tier
// or tiers aren't loaded.
export const trackGttBuyQtyEntry = ({
  ecommerceGtmEvent,
  productId,
  productName,
  quantity,
  orderValue,
  volumeTier,
}) => {
  ecommerceGtmEvent(GTT_BUY_QTY_ENTRY, {
    product_id: productId ?? null,
    product_name: productName ?? null,
    quantity: quantity ?? null,
    order_value: orderValue ?? null,
    volume_tier: volumeTier ?? null,
  });
};

// gtt_buy_qty_stepper — the user used the +/- stepper buttons instead of
// typing a quantity. Same shape as trackGttBuyQtyEntry — kept as a separate
// event/function (not a shared one with an interaction-method field) because
// the AC defines them as two distinct events.
export const trackGttBuyQtyStepper = ({
  ecommerceGtmEvent,
  productId,
  productName,
  quantity,
  orderValue,
  volumeTier,
}) => {
  ecommerceGtmEvent(GTT_BUY_QTY_STEPPER, {
    product_id: productId ?? null,
    product_name: productName ?? null,
    quantity: quantity ?? null,
    order_value: orderValue ?? null,
    volume_tier: volumeTier ?? null,
  });
};

// gtt_buy_vault_location — the user picked a vault in the vault-selection
// drawer (shared with the regular Buy flow — only fired for a GTT order).
export const trackGttBuyVaultLocation = ({
  ecommerceGtmEvent,
  productId,
  productName,
  vaultLocation,
}) => {
  ecommerceGtmEvent(GTT_BUY_VAULT_LOCATION, {
    product_id: productId ?? null,
    product_name: productName ?? null,
    vault_location: vaultLocation ?? null,
  });
};

// gtt_buy_insufficient_funds — the entered target price × quantity exceeded
// the available balance (the order-entry CTA relabels to "Add Funds" at this
// point). Fired alongside trackGttBuyAddFundsTapped from the same submit
// click, since that's the only discrete moment this codebase has for "the
// check failed" — there's no separate signal for the check failing without
// the user also having clicked submit.
export const trackGttBuyInsufficientFunds = ({
  ecommerceGtmEvent,
  productId,
  productName,
  orderValue,
  availableBalance,
  shortfallAmount,
}) => {
  ecommerceGtmEvent(GTT_BUY_INSUFFICIENT_FUNDS, {
    product_id: productId ?? null,
    product_name: productName ?? null,
    order_value: orderValue ?? null,
    available_balance: availableBalance ?? null,
    shortfall_amount: shortfallAmount ?? null,
  });
};

// gtt_buy_add_funds_tapped — the user clicked the relabelled "Add Funds" CTA
// to open the deposit wizard. Supersedes the older trigger_order_add_funds_
// clicked (dropped — same moment, different key names, no remaining
// consumer). Fired on click only: the deposit wizards report no completion,
// so there is no success signal to hang a follow-up event on.
export const trackGttBuyAddFundsTapped = ({
  ecommerceGtmEvent,
  productId,
  productName,
  orderValue,
  shortfallAmount,
}) => {
  ecommerceGtmEvent(GTT_BUY_ADD_FUNDS_TAPPED, {
    product_id: productId ?? null,
    product_name: productName ?? null,
    order_value: orderValue ?? null,
    shortfall_amount: shortfallAmount ?? null,
  });
};

// gtt_buy_current_price_tapped — the user used the "Buy at Current Price?"
// escape hatch to switch from GTT into the regular Buy flow. entryPoint is
// the GTT journey's own entry point (wizardData.entryPoint), read directly
// since the switch-over hasn't happened yet at the point this fires.
export const trackGttBuyCurrentPriceTapped = ({
  ecommerceGtmEvent,
  productId,
  productName,
  currentPrice,
  entryPoint,
}) => {
  ecommerceGtmEvent(GTT_BUY_CURRENT_PRICE_TAPPED, {
    product_id: productId ?? null,
    product_name: productName ?? null,
    current_price: currentPrice ?? null,
    entry_point: entryPoint ?? null,
  });
};
